import { useCallback, useEffect, useRef, useState } from 'react';
import syncOwnProfile from '../api/kyc/syncOwnProfile';
import { IBAN_GATE_EVENTS } from './useIbanGate';

/**
 * Marcador que la `returnUrl` trae al volver del mini-KYC. Lo pone quien manda
 * al usuario, y es lo único que distingue «vuelve del KYC» de «entra al
 * dashboard» — el KYC vive en otro dominio y no puede avisarnos de otra forma.
 */
export const IBAN_GATE_RETURN_PARAM = 'ibanGateDone';

export interface UseIbanGateReturnParams {
	token: string | null | undefined;
	/** Si esta carga viene de vuelta del mini-KYC. */
	returning: boolean;
	/** Qué hacer cuando el perfil ya está sincronizado (refrescar `current-user`). */
	onDone: () => void;
}

export interface UseIbanGateReturnResult {
	/** El sync está en vuelo. */
	syncing: boolean;
	/** El IBAN NO llegó a comparsa: el desvío no ha servido. */
	failed: boolean;
	/** Importe que el usuario había elegido antes de irse, para retomarlo. */
	pendingAmount: number | null;
}

/**
 * La vuelta del mini-KYC: sincronizar, esperar, y solo entonces refrescar.
 *
 * El orden importa y no es un detalle. El IBAN que el usuario acaba de dar vive
 * en el expediente de Supabase, no en comparsa: hasta que `sync-profile/me` no
 * lo copia, `current-user` sigue diciendo que no tiene cuenta. Refrescar en
 * paralelo devolvería al usuario **el mismo gate que acaba de completar**.
 *
 * Por eso `onDone` se llama DESPUÉS del sync, nunca a la vez.
 */
const useIbanGateReturn = ({
	token,
	returning,
	onDone
}: UseIbanGateReturnParams): UseIbanGateReturnResult => {
	const [syncing, setSyncing] = useState(false);
	const [failed, setFailed] = useState(false);
	const [pendingAmount, setPendingAmount] = useState<number | null>(null);

	// Una sola vez por vuelta: el efecto depende de `onDone`, que el consumidor
	// puede recrear en cada render, y sin esto se sincronizaría en bucle.
	const done = useRef(false);

	const readPendingAmount = useCallback(() => {
		const raw = sessionStorage.getItem(IBAN_GATE_EVENTS.PENDING_AMOUNT_KEY);
		// Se lee y se limpia: es de este desvío, no de la próxima visita.
		sessionStorage.removeItem(IBAN_GATE_EVENTS.PENDING_AMOUNT_KEY);

		const amount = Number(raw);
		return raw && Number.isFinite(amount) && amount > 0 ? amount : null;
	}, []);

	useEffect(() => {
		if (!returning || !token || done.current) return;
		done.current = true;

		setPendingAmount(readPendingAmount());
		setSyncing(true);

		syncOwnProfile(token)
			.then((res) => {
				// `filledFields` vacío significa que el campo ya tenía valor o que
				// no había expediente: en ninguno de los dos casos podemos dar por
				// bueno el desvío, porque el usuario acabaría en el mismo 500.
				const llegó = !!res.data?.filledFields?.includes('bankAccount');
				setFailed(!llegó);
				if (llegó) onDone();
			})
			.catch(() => {
				// Un fallo de red no puede pasar por éxito: es exactamente el caso
				// en el que el IBAN NO está donde la plataforma lo busca.
				setFailed(true);
			})
			.finally(() => setSyncing(false));
	}, [returning, token, onDone, readPendingAmount]);

	return { syncing, failed, pendingAmount };
};

export default useIbanGateReturn;
